<template>
  <a-button
    :type="variant"
    :loading="submitting"
    class="aha-antd-button"
    :class="[
      {
        'btn-underline': underline,
        'disabled': disabled,
        'is-loading-inline': isLoadingInline,
        'has-icon': iconName.length && !submitting,
        'active': active
      },
      `ant-btn-${variant}`,
      `ant-btn-${size}`,
      'ant-btn-custom'

    ]"
    :disabled="disabled"
    :ghost="ghost"
    :icon="antIcon"
    :block="block"
    @click="$emit('click', $event)"
  >
    <aha-icon
      :name="iconName"
      :height="iconSize"
      :width="iconSize"
      :viewBox="iconViewBox"
      :maintainColor="iconMaintainColor"
      :class="iconClasses"
      v-if="iconName.length && !submitting"
    />
    <div v-if="$slots.default && $slots.default.length" class="--content-container" :class="{ 'invisible': isLoadingInline && submitting }">
      <slot />
    </div>
  </a-button>
</template>

<script>
import { Button } from 'ant-design-vue'
import { ICON } from '../../../utils/constants'

export default {
  components: {
    'a-button': Button,
  },
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator(value) {
        return ['primary', 'secondary', 'tertiary', 'success', 'danger', 'link', 'essential', 'pro', 'text', 'text-link', 'primary-alt'].includes(value)
      },
    },
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['sm', 'md', 'lg', 'xl'].includes(value)
      },
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    iconName: {
      type: String,
      default: '',
    },
    block: {
      type: Boolean,
      default: false,
    },
    underline: {
      type: Boolean,
      default: false,
    },
    iconViewBox: {
      type: String,
      default: '0 0 24 24',
    },
    iconClasses: {
      type: String,
      default: '',
    },
    isLoadingInline: {
      type: Boolean,
      default: false,
    },
    ghost: {
      type: Boolean,
      default: false,
    },
    antIcon: {
      type: String,
      default: '',
    },
    iconMaintainColor: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    iconSize() {
      return this.size === 'sm' || this.size === 'md' ? ICON.MD_SIZE : ICON.LG_SIZE
    },
  },
}
</script>

<style scoped lang="scss">
.aha-antd-button {
  @apply flex justify-center items-center gap-2;

  /* Primary Button Colors */
  --btn-primary-bg: theme('colors.purple.60');
  --btn-primary-text: theme('colors.gray.0');
  --btn-primary-border: theme('colors.purple.60');
  --btn-primary-hover-bg: theme('colors.purple.50');
  --btn-primary-hover-border: theme('colors.purple.50');
  --btn-primary-active-bg: theme('colors.purple.80');
  --btn-primary-active-border: theme('colors.purple.80');
  --btn-primary-focus-shadow: theme('colors.purple.30');

  /* Primary Alt Button Colors */
  --btn-primary-alt-bg: theme('colors.purple.60');
  --btn-primary-alt-text: theme('colors.gray.0');
  --btn-primary-alt-border: theme('colors.purple.90');
  --btn-primary-alt-hover-bg: theme('colors.purple.50');
  --btn-primary-alt-hover-border: theme('colors.purple.90');
  --btn-primary-alt-active-bg: theme('colors.purple.80');
  --btn-primary-alt-active-border: theme('colors.purple.80');
  --btn-primary-alt-focus-shadow: theme('colors.purple.30');

  /* Secondary Button Colors */
  --btn-secondary-bg: theme('colors.gray.0');
  --btn-secondary-text: theme('colors.gray.100');
  --btn-secondary-border: theme('colors.gray.40');
  --btn-secondary-hover-bg: theme('colors.purple.10');
  --btn-secondary-hover-text: theme('colors.purple.50');
  --btn-secondary-hover-border: theme('colors.purple.30');
  --btn-secondary-active-bg: theme('colors.purple.15');
  --btn-secondary-active-text: theme('colors.purple.60');
  --btn-secondary-active-border: theme('colors.purple.40');

   /* Essential Button Styles */
  --btn-essential-bg: theme('colors.coral.50');
  --btn-essential-active-bg: theme('colors.coral.60');
  --btn-essential-hover-bg: theme('colors.coral.40');

   /* Pro Button Styles */
  --btn-pro-bg: theme('colors.emerald.60');
  --btn-pro-active-bg: theme('colors.emerald.70');
  --btn-pro-hover-bg: theme('colors.emerald.50');

  /* Disabled Button Colors */
  --btn-disabled-bg: theme('colors.gray.40');
  --btn-disabled-text: theme('colors.gray.60');
  --btn-disabled-border: theme('colors.gray.40');

  /* Focus State */
  --btn-focus-shadow-size: 0px 0px 0px 4px;
}

.ant-btn-custom {
  @apply relative border-0;

  &.disabled {
    color: var(--btn-disabled-text);
    @apply cursor-not-allowed;
  }

  &:focus-visible {
    border-color: transparent;
    box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-focus-shadow);
    outline: none;
  }

  &.ant-btn-secondary  {
    font-weight: 600;
    border: 1px solid var(--btn-secondary-border);
    color: var(--btn-secondary-text);
    background-color: var(--btn-secondary-bg);

    &.disabled {
      border-color: var(--btn-disabled-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-disabled-bg);

      &:hover, &:active, &:focus {
        border-color: var(--btn-disabled-border);
        color: var(--btn-disabled-text);
        background-color: var(--btn-disabled-bg);
        box-shadow: none;
      }
    }

    &:not(.disabled) {
      &:active, &.active {
        border-color: var(--btn-secondary-active-border);
        color: var(--btn-secondary-active-text);
        background-color: var(--btn-secondary-active-bg);
      }
      &:hover, &:focus {
        border-color: var(--btn-secondary-hover-border);
        background-color: var(--btn-secondary-hover-bg);
        color: var(--btn-secondary-hover-text);
      }
      &:focus-visible {
        color: var(--btn-secondary-text);
        border-color: transparent;
        background-color: var(--btn-secondary-bg);
        box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-focus-shadow);
      }
    }

    &.ant-btn-loading {
      border-color: var(--btn-secondary-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-secondary-bg);
    }
  }

  &.ant-btn-success  {
    font-weight: 600;
    background-color: theme('colors.button.successBg');
    color: theme('colors.button.successText');

    &:active {
      background-color: theme('colors.button.successBgActive');
    }
    &:hover {
      background-color: theme('colors.button.successBgHover');
      color: theme('colors.button.text');
    }
    &:focus {
      background-color: theme('colors.button.successBgFocus');
    }
    &:focus-visible {
      border: none;
    }
  }

  &.ant-btn-primary {
    font-weight: 600;
    border: 1px solid var(--btn-primary-border);
    color: var(--btn-primary-text);
    background-color: var(--btn-primary-bg);

    &.disabled {
      border-color: var(--btn-disabled-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-disabled-bg);

      &:hover, &:active, &:focus {
        border-color: var(--btn-disabled-border);
        color: var(--btn-disabled-text);
        background-color: var(--btn-disabled-bg);
        box-shadow: none;
      }
    }

    &:not(.disabled) {
      &:hover, &:focus {
        border-color: var(--btn-primary-hover-border);
        background-color: var(--btn-primary-hover-bg);
        color: var(--btn-primary-text);
      }
      &:active, &.active {
        border-color: var(--btn-primary-active-border);
        background-color: var(--btn-primary-active-bg);
        color: var(--btn-primary-text);
      }
      &:focus-visible {
        border-color: transparent;
        background-color: var(--btn-primary-bg);
        color: var(--btn-primary-text);
        box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-focus-shadow);
      }
    }

    &.ant-btn-loading {
      border-color: var(--btn-primary-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-primary-bg);
    }
  }

  &.ant-btn-primary-alt {
    font-weight: 600;
    border: 1px solid var(--btn-primary-alt-border);
    color: var(--btn-primary-alt-text);
    background-color: var(--btn-primary-alt-bg);

    &.disabled {
      border-color: var(--btn-disabled-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-disabled-bg);

      &:hover, &:active, &:focus {
        border-color: var(--btn-disabled-border);
        color: var(--btn-disabled-text);
        background-color: var(--btn-disabled-bg);
        box-shadow: none;
      }
    }

    &:not(.disabled) {
      &:hover, &:focus {
        border-color: var(--btn-primary-alt-hover-border);
        background-color: var(--btn-primary-alt-hover-bg);
        color: var(--btn-primary-alt-text);
      }
      &:active, &.active {
        border-color: var(--btn-primary-alt-active-border);
        background-color: var(--btn-primary-alt-active-bg);
        color: var(--btn-primary-alt-text);
      }
      &:focus-visible {
        border-color: transparent;
        background-color: var(--btn-primary-alt-bg);
        color: var(--btn-primary-alt-text);
        box-shadow: var(--btn-focus-shadow-size) var(--btn-primary-alt-focus-shadow);
      }
    }

    &.ant-btn-loading {
      border-color: var(--btn-primary-alt-border);
      color: var(--btn-disabled-text);
      background-color: var(--btn-primary-alt-bg);
    }
  }

  &.ant-btn-link {
    color: #348EF6;
    &:hover {
      color: #4F9BF3;
    }
    &:active {
      color: #1162C0;
    }
  }
  &.ant-btn-danger {
    font-weight: 600;
    background-color: #D43F48;
    color: theme('colors.button.dangerText');

    &:hover {
      background-color: theme('colors.button.dangerBgHover');
      color: theme('colors.button.dangerText');
    }
    &:focus {
      background-color: theme('colors.button.dangerBgFocus');
    }
  }
  &.ant-btn-tertiary {
    font-weight: 600;
    border: none;
    box-shadow: none;
    background-color: transparent;
    &:hover {
      color: unset;
      background-color: #E0E3ED;
    }
    &:active {
      color: unset;
      background-color: #D4D7E0;
    }
    &:focus {
      color: unset;
    }
    &:focus-visible {
      color: unset;
    }
    &.disabled {
      color: #9D9D9D;
      background-color: transparent;
    }

    &.ant-btn-sm {
      padding: 4px 4px;
      gap: 4px;

      &.has-icon {
        padding-left: 2px;
      }
    }

    &.ant-btn-md {
      padding: 8px 8px;
      gap: 6px;

      &.has-icon {
        padding-left: 6px;
      }
    }

    &.ant-btn-lg {
      padding: 12px 10px;
      gap: 6px;

      &.has-icon {
        padding-left: 8px;
      }
    }

    &.ant-btn-xl {
      padding: 12px 10px;
      gap: 6px;

      &.has-icon {
        padding-left: 8px;
      }
    }
  }
  &.ant-btn-text {
    border: none;
    box-shadow: none;
    &:hover {
      color: unset;
      background-color: #E0E3ED;
    }
    &:active {
      color: unset;
      background-color: #D4D7E0;
    }
    &:focus {
      color: unset;
    }
    &:focus-visible {
      color: unset;
    }
  }
  &.ant-btn-text-link {
    border: none;
    box-shadow: none;
    color: #191F2D;
    &:hover {
      color: #2E7FDB;
    }
    &:active {
      color: #0F56A8;
    }
    &:focus-visible {
      color: #191F2D;
    }
  }
  &.ant-btn-essential {
    font-weight: 600;
    background-color: var(--btn-essential-bg);
    color: var(--btn-primary-text);

    &:active {
      background-color: var(--btn-essential-active-bg);
    }
    &:hover {
      background-color: var(--btn-essential-hover-bg);
      color: var(--btn-primary-text);
    }
  }

  &.ant-btn-pro {
    font-weight: 600;
    background-color: var(--btn-pro-bg);
    color: var(--btn-primary-text);

    &:active {
      background-color: var(--btn-pro-active-bg);
    }
    &:hover {
      background-color: var(--btn-pro-hover-bg);
      color: var(--btn-primary-text);
    }
  }

  .--content-container {
    line-height: 1.5;
  }

  .disabled {
    cursor: not-allowed;
  }
}

.is-loading-inline {
  ::v-deep {
    .anticon {
      position: absolute;
      left: 0;
      right: 0;
      margin: auto !important;
      width: 16px;
    }
  }
}

::v-deep {
  .anticon {
    width: 16px;
  }
}

.btn-underline {
  ::v-deep {
    span {
      text-decoration: underline;
    }
  }
}
.ant-btn-sm {
  height: 28px;
  padding: 4px 8px;
  border-radius: 4px;
  gap: 4px;

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 20px;
  }

  &.has-icon {
    padding-left: 6px;
  }
  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 8px;
  }
}
.ant-btn-md {
  height: 36px;
  padding: 8px 16px;
  border-radius: 8px;

  &.has-icon {
    padding-left: 14px;
  }

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 28px;
  }

  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 16px;
  }
}
.ant-btn-lg {
  height: 44px;
  padding: 12px 20px;
  border-radius: 8px;

  &.has-icon {
    padding-left: 18px;
  }

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 36px;
  }
  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 20px;
  }
}
.ant-btn-xl {
  height: 52px;
  padding: 12px 20px;
  border-radius: 12px;
  font-size: 24px;
  gap: 12px;

  &.has-icon {
    padding-left: 18px;
  }

  &.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 36px;
  }

  &.is-loading-inline.ant-btn-loading:not(.ant-btn-circle):not(.ant-btn-circle-outline):not(.ant-btn-icon-only) {
    padding-left: 20px;
  }
}
</style>
